import React from 'react';
import { motion } from 'framer-motion';
import { MapPinIcon, EnvelopeIcon, PhoneIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const ContactSection = () => {
  return (
    <section className="bg-white py-20 relative overflow-hidden font-sans">
      
      {/* Decorative Background Elements */}
      <div className="absolute -top-24 right-0 -z-10 transform-gpu overflow-hidden blur-3xl" aria-hidden="true">
        <div className="aspect-[1404/767] w-[87.75rem] bg-gradient-to-tr from-[#ffe4e6] to-[#fce7f3] opacity-50"></div>
      </div>
      <div className="absolute -bottom-24 left-0 -z-10 transform-gpu overflow-hidden blur-3xl" aria-hidden="true">
        <div className="aspect-[1404/767] w-[87.75rem] bg-gradient-to-tr from-[#f3f4f6] to-[#e5e7eb] opacity-40"></div>
      </div>

      <div className="mx-auto max-w-7xl px-6 lg:px-8 relative z-10">
        
        {/* Header (Aligned with Testimonial/FAQ sizing) */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mx-auto max-w-2xl text-center mb-5 sm:mb-7"
        >
          <h2 className="text-[13px] font-bold uppercase tracking-wide text-[#dc343b] mb-3">Get in Touch</h2>
          <p className="text-3xl font-extrabold tracking-tight text-[#111827] sm:text-[2rem]">
            Let's grow your business together.
          </p>
          <p className="mt-4 text-[15px] leading-relaxed text-gray-500 font-medium max-w-2xl mx-auto">
            Have questions about our enterprise plans, custom integrations, or need technical support? Our dedicated team is here to help you succeed.
          </p>
        </motion.div>

        <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-2 items-start">
          
          {/* Left: Contact Info Cards */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, ease: "easeOut" }}
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-8"
          >
            {/* Email Card */}
            <div className="flex flex-col sm:flex-row lg:flex-row gap-6 p-8 rounded-xl bg-gray-50 border border-gray-100 hover:bg-white hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-gray-200 transition-all duration-300 group">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm border border-gray-100 group-hover:bg-[#dc343b] transition-colors">
                <EnvelopeIcon className="h-6 w-6 text-[#dc343b] group-hover:text-white transition-colors" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Chat with sales</h3>
                <p className="mt-2 text-[15px] text-gray-500 leading-relaxed">Speak to our friendly team about enterprise solutions.</p>
                <a href="mailto:sales@khatabook.com" className="mt-4 inline-flex items-center text-[15px] font-semibold text-[#dc343b] hover:text-[#b91c1c]">
                  sales@khatabook.com &rarr;
                </a>
              </div>
            </div>

            {/* Phone Card */}
            <div className="flex flex-col sm:flex-row lg:flex-row gap-6 p-8 rounded-xl bg-gray-50 border border-gray-100 hover:bg-white hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-gray-200 transition-all duration-300 group">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm border border-gray-100 group-hover:bg-[#dc343b] transition-colors">
                <PhoneIcon className="h-6 w-6 text-[#dc343b] group-hover:text-white transition-colors" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Call us directly</h3>
                <p className="mt-2 text-[15px] text-gray-500 leading-relaxed">Mon-Fri from 9am to 6pm IST for immediate support.</p>
                <a href="tel:+918001234567" className="mt-4 inline-flex items-center text-[15px] font-semibold text-[#dc343b] hover:text-[#b91c1c]">
                  +91 (800) 123-4567 &rarr;
                </a>
              </div>
            </div>

            {/* Location Card */}
            <div className="flex flex-col sm:flex-row lg:flex-row gap-6 p-8 rounded-xl bg-gray-50 border border-gray-100 hover:bg-white hover:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:border-gray-200 transition-all duration-300 group sm:col-span-2 lg:col-span-1">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-2xl bg-white shadow-sm border border-gray-100 group-hover:bg-[#dc343b] transition-colors">
                <MapPinIcon className="h-6 w-6 text-[#dc343b] group-hover:text-white transition-colors" aria-hidden="true" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">Visit our office</h3>
                <p className="mt-2 text-[15px] text-gray-500 leading-relaxed">Visit our HQ for an in-person meeting.</p>
                <p className="mt-4 text-[15px] font-semibold text-gray-900">100 Feet Road, Indiranagar, Bengaluru 560038</p>
              </div>
            </div>

          </motion.div>

          {/* Right: Modern Contact Form */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6, delay: 0.2, ease: "easeOut" }}
            className="bg-white rounded-xl p-8 sm:p-8 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.1)] border border-gray-100 relative lg:ml-10"
          >
            {/* Soft decorative blur behind form */}
            <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white to-gray-50/50 rounded-xl"></div>

            <form action="#" method="POST" className="space-y-8">
              <div className="grid grid-cols-1 gap-x-8 gap-y-8 sm:grid-cols-2">
                <div className="sm:col-span-2">
                  <label htmlFor="name" className="block text-[13px] font-bold tracking-wide text-gray-900 uppercase mb-2">Full Name</label>
                  <div className="relative">
                    <input type="text" name="name" id="name" autoComplete="name" className="block w-full rounded-xl border-0 px-5 py-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#dc343b] sm:text-[15px] bg-gray-50/50 hover:bg-gray-50 transition-colors" placeholder="e.g. John Doe" />
                  </div>
                </div>
                
                <div className="sm:col-span-2">
                  <label htmlFor="email" className="block text-[13px] font-bold tracking-wide text-gray-900 uppercase mb-2">Work Email</label>
                  <div className="relative">
                    <input type="email" name="email" id="email" autoComplete="email" className="block w-full rounded-xl border-0 px-5 py-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#dc343b] sm:text-[15px] bg-gray-50/50 hover:bg-gray-50 transition-colors" placeholder="john@company.com" />
                  </div>
                </div>

                <div className="sm:col-span-2">
                  <label htmlFor="message" className="block text-[13px] font-bold tracking-wide text-gray-900 uppercase mb-2">How can we help?</label>
                  <div className="relative">
                    <textarea name="message" id="message" rows={4} className="block w-full rounded-xl border-0 px-5 py-4 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-200 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-[#dc343b] sm:text-[15px] bg-gray-50/50 hover:bg-gray-50 transition-colors resize-none" placeholder="Tell us about your business requirements..." defaultValue={''} />
                  </div>
                </div>
              </div>
              <div className="pt-2">
                <button type="button" className="flex w-full justify-center items-center rounded-xl bg-[#dc343b] px-4 py-4 text-[15px] font-bold text-white shadow-[0_8px_30px_rgba(220,52,59,0.25)] hover:shadow-[0_8px_30px_rgba(220,52,59,0.4)] hover:bg-[#b91c1c] hover:-translate-y-0.5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#dc343b] transition-all group">
                  Send Message
                  <ArrowRightIcon className="ml-2 h-4 w-4 transform group-hover:translate-x-1 transition-transform" aria-hidden="true" />
                </button>
              </div>
            </form>
          </motion.div>

        </div>
      </div>
    </section>
  );
};

export default ContactSection;
